import { cn } from '@rocco/utils/cn';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@rocco/components/select';
import {
    GATE_FIELD_INPUT_CLASS,
    GateFieldError,
    GateFieldLabel,
} from '~/modules-rocco/user/component/auth-modal/GateFormControls';

export interface GateSelectOption {
    value: string;
    label: string;
}

export interface GateSelectFieldProps {
    label: string;
    placeholder: string;
    /** `''` shows the placeholder. */
    value: string;
    options: readonly GateSelectOption[];
    error?: string | null;
    disabled?: boolean;
    /** Taller list for long option sets such as countries. */
    tall?: boolean;
    className?: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
}

/**
 * A labelled single-choice field in the gate's field tokens: the grey
 * borderless trigger of the gate inputs, opening a list sized to the trigger.
 */
export const GateSelectField = ({
    label,
    placeholder,
    value,
    options,
    error,
    disabled,
    tall = false,
    className,
    onChange,
    onBlur,
}: GateSelectFieldProps) => (
    <div className={cn('flex flex-col gap-1.5', className)}>
        <GateFieldLabel>{label}</GateFieldLabel>
        {/* Radix keeps the last value when this goes `undefined`, so an unset
            answer is fed as '' to show the placeholder. */}
        <Select value={value} disabled={disabled} onValueChange={onChange}>
            <SelectTrigger
                aria-label={label}
                aria-invalid={!!error}
                className={cn(
                    GATE_FIELD_INPUT_CLASS,
                    'w-full justify-between data-[placeholder]:text-black/30',
                    error && 'ring-1 ring-destructive',
                )}
                onBlur={onBlur}
            >
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            {/* Portalled with `position="popper"`, so the dialog's `translate`
                centring cannot mis-anchor it. Width and alignment follow the
                trigger so it reads as the field opening. */}
            <SelectContent
                align="start"
                className={cn(
                    'w-(--radix-select-trigger-width)',
                    tall && 'max-h-72',
                )}
            >
                {options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                        {option.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
        <GateFieldError message={error ?? undefined} />
    </div>
);
